import fullLogo from "@/assets/full-logo.png";
import { useCallback, useEffect, useRef, useState } from "react";
import { isVideoPoster, shouldUsePosterDurationTimer } from "../lib/display-poster-slideshow";

type Poster = {
  id: string;
  url: string;
  mediaType: "image" | "video";
  durationSec: number;
};

function VideoSlide({
  url,
  loop,
  onFinished,
}: {
  url: string;
  loop: boolean;
  onFinished: () => void;
}) {
  const ref = useRef<HTMLVideoElement>(null);
  const finishedRef = useRef(false);

  useEffect(() => {
    finishedRef.current = false;
    const el = ref.current;
    if (!el) return;

    const startPlayback = () => {
      el.currentTime = 0;
      void el.play().catch(() => {
        const retry = () => {
          el.removeEventListener("canplay", retry);
          void el.play().catch(() => {
            if (!loop) onFinished();
          });
        };
        el.addEventListener("canplay", retry);
        el.load();
      });
    };

    startPlayback();

    return () => {
      el.pause();
      el.currentTime = 0;
    };
  }, [url, loop, onFinished]);

  const handleEnded = useCallback(() => {
    if (loop) return;
    if (finishedRef.current) return;
    finishedRef.current = true;
    onFinished();
  }, [loop, onFinished]);

  return (
    <video
      ref={ref}
      src={url}
      muted
      playsInline
      loop={loop}
      preload="auto"
      className="absolute inset-0 h-full w-full object-contain"
      onEnded={handleEnded}
    />
  );
}

export function DisplayPosterSlideshow({ posters }: { posters: Poster[] }) {
  const [index, setIndex] = useState(0);
  const posterKey = posters.map((p) => p.id).join("|");

  useEffect(() => {
    setIndex(0);
  }, [posterKey]);

  const goNext = useCallback(() => {
    if (posters.length <= 1) return;
    setIndex((i) => (i + 1) % posters.length);
  }, [posters.length]);

  const current = posters[index];

  useEffect(() => {
    if (!current || !shouldUsePosterDurationTimer(current, posters.length)) {
      return;
    }

    const ms = Math.max(5, current.durationSec) * 1000;
    const id = window.setTimeout(goNext, ms);
    return () => window.clearTimeout(id);
  }, [current, goNext, index, posters.length]);

  if (posters.length === 0) {
    return (
      <div className="flex h-full w-full items-center justify-center bg-neutral-100">
        <img
          src={fullLogo}
          alt="Fanglao Studio"
          className="h-auto w-[min(55%,28rem)] max-w-[80%] object-contain opacity-90"
        />
      </div>
    );
  }

  return (
    <div className="relative h-full w-full overflow-hidden bg-neutral-100">
      {posters.map((poster, i) => {
        const active = i === index;

        if (isVideoPoster(poster)) {
          if (!active) return null;

          return (
            <VideoSlide
              key={poster.id}
              url={poster.url}
              loop={posters.length <= 1}
              onFinished={goNext}
            />
          );
        }

        return (
          <img
            key={poster.id}
            src={poster.url}
            alt=""
            className="absolute inset-0 h-full w-full object-contain transition-opacity duration-300"
            style={{ opacity: active ? 1 : 0 }}
          />
        );
      })}
    </div>
  );
}
